import { getClassLevel } from './classIdentity.mjs';

// 2024 Ranger rules (SRD 5.2): level-derived values, the Wisdom-limited uses of Tireless and Nature's Veil, and the
// Hunter's Mark features of levels 13, 17 and 20.
export const HUNTERS_MARK_SPELL_ID = 'hunter-s-mark';
/** Feature grant for Favored Enemy's always-prepared Hunter's Mark (free casts or slots). */
export const FAVORED_ENEMY_GRANT_ID = 'ranger-favored-enemy-hunter-s-mark';

export const RANGER_FEATURE_LEVELS = Object.freeze({
  spellcasting: 1,
  favoredEnemy: 1,
  weaponMastery: 1,
  deftExplorer: 2,
  fightingStyle: 2,
  subclass: 3,
  extraAttack: 5,
  roving: 6,
  tireless: 10,
  relentlessHunter: 13,
  naturesVeil: 14,
  preciseHunter: 17,
  feralSenses: 18,
  foeSlayer: 20,
});

export const getRangerLevel = character => getClassLevel(character, 'ranger');

export const hasRangerFeature = (character, feature) => {
  const level = getRangerLevel(character);
  const required = RANGER_FEATURE_LEVELS[feature];
  return level > 0 && Number.isInteger(required) && level >= required;
};

/** Hunter's Mark casts without a spell slot per Long Rest (Favored Enemy column). */
export const favoredEnemyUses = level => (level < 1 ? 0 : level < 5 ? 2 : level < 9 ? 3 : level < 13 ? 4 : level < 17 ? 5 : 6);
/** Free Favored Enemy casts left this Long Rest, counted in spellcastingState.speciesFreeCasts. */
export const favoredEnemyCastsLeft = character => Math.max(0, favoredEnemyUses(getRangerLevel(character))
  - Math.max(0, Number(character?.spellcastingState?.speciesFreeCasts?.[FAVORED_ENEMY_GRANT_ID]) || 0));

/** Roving (6): +10 feet of Speed out of Heavy armor. The Climb and Swim Speeds equal Speed in any armor. */
export const rovingSpeedBonus = (character, wearingHeavyArmor = false) => (hasRangerFeature(character, 'roving') && !wearingHeavyArmor ? 10 : 0);

/**
 * Tireless (10) and Nature's Veil (14): uses equal to the Wisdom modifier (minimum of once), all back on a Long Rest,
 * saved at `classState.ranger.<key>` as uses spent.
 */
export const RANGER_WISDOM_USES = Object.freeze({
  tireless: Object.freeze({ feature: 'tireless', key: 'tireless', name: 'Tireless' }),
  'natures-veil': Object.freeze({ feature: 'naturesVeil', key: 'naturesVeil', name: 'Nature\u2019s Veil' }),
});
export const rangerWisdomUses = (character, useId, wisdomModifier) => {
  const use = RANGER_WISDOM_USES[useId];
  if (!use || !hasRangerFeature(character, use.feature)) return { available: false, maximum: 0, spent: 0, remaining: 0, revision: 0 };
  const maximum = Math.max(1, Math.trunc(Number(wisdomModifier) || 0));
  const state = character?.classState?.ranger?.[use.key] || {};
  const spent = Math.min(maximum, Math.max(0, Math.trunc(Number(state.spent) || 0)));
  return { available: true, maximum, spent, remaining: maximum - spent, revision: Math.max(0, Math.trunc(Number(state.revision) || 0)) };
};
/** The uses a Long Rest restores, as `classState.ranger.<key>` values; empty when nothing was spent. */
export const rangerRestState = (character, type) => (type !== 'long' ? {} : Object.fromEntries(Object.values(RANGER_WISDOM_USES)
  .map(({ key }) => [key, character?.classState?.ranger?.[key]]).filter(([, state]) => Number(state?.spent) > 0)
  .map(([key, state]) => [key, { spent: 0, revision: (Number(state.revision) || 0) + 1 }])));
/** Tireless: 1d8 plus the Wisdom modifier (minimum of 1). */
export const tirelessTemporaryHitPoints = (roll, wisdomModifier) => Math.max(0, Math.trunc(Number(roll) || 0)) + Math.max(1, Math.trunc(Number(wisdomModifier) || 0));
export const TIRELESS_DIE = 8;
export const NATURES_VEIL_FEATURE_ID = 'ranger:natures-veil';
/** Nature's Veil: Invisible until the end of the Ranger's next turn (the turn it is used on doesn't count). */
export const naturesVeilExpiration = combatantId => ({ type: 'targetTurn', combatantId: String(combatantId), boundary: 'end', remainingOccurrences: 1 });

/** Feral Senses (18): Blindsight 30 feet. A creature in range is perceived even when it is Invisible or heavily obscured. */
export const FERAL_SENSES_RANGE_FEET = 30;
export const feralSensesPerceive = (character, distanceFeet) => hasRangerFeature(character, 'feralSenses')
  && Number.isFinite(distanceFeet) && distanceFeet <= FERAL_SENSES_RANGE_FEET;

/** The per-target recipient effect of Hunter's Mark, and its Concentration root. */
export const HUNTERS_MARK_EFFECT_ID = 'spell:hunter-s-mark';
/** True when this attacker's Hunter's Mark is on the target (the per-target recipient effect). */
export const isMarkedByAttacker = (activeEffects, attackerId, targetId) => (Array.isArray(activeEffects) ? activeEffects : []).some((effect) =>
  effect?.definitionId === HUNTERS_MARK_EFFECT_ID && effect.targetCombatantId != null
  && String(effect.targetCombatantId) === String(targetId) && String(effect.sourceCombatantId) === String(attackerId));
/** This Ranger's Hunter's Mark is on a creature now. */
export const hasActiveHuntersMark = (activeEffects, combatantId) => (Array.isArray(activeEffects) ? activeEffects : []).some((effect) =>
  effect?.definitionId === HUNTERS_MARK_EFFECT_ID && effect.targetCombatantId != null && String(effect.sourceCombatantId) === String(combatantId));
/** Foe Slayer (20): the Hunter's Mark die is a d10 rather than a d6. */
export const huntersMarkDieSides = character => (hasRangerFeature(character, 'foeSlayer') ? 10 : 6);
/** Precise Hunter (17): Advantage on attack rolls against the creature marked by your Hunter's Mark. */
export const PRECISE_HUNTER_ADVANTAGE_SOURCE = 'Ranger: Precise Hunter';
export const preciseHunterApplies = (character, activeEffects, attackerId, targetId) => hasRangerFeature(character, 'preciseHunter')
  && isMarkedByAttacker(activeEffects, attackerId, targetId);
/** Relentless Hunter (13): taking damage can't break Concentration on Hunter's Mark. */
export const concentrationIgnoresDamage = (character, root) => root?.definitionId === HUNTERS_MARK_EFFECT_ID && hasRangerFeature(character, 'relentlessHunter');

/** Multiattack Defense (Hunter 7): after a hit, the attacker has Disadvantage on its other attack rolls against the Ranger this turn. */
export const MULTIATTACK_DEFENSE_EFFECT_ID = 'ranger:multiattack-defense';
export const MULTIATTACK_DEFENSE_DISADVANTAGE_SOURCE = 'Target: Multiattack Defense';
export const multiattackDefenseApplies = (activeEffects, attackerId, targetId) => (Array.isArray(activeEffects) ? activeEffects : []).some(effect =>
  effect?.definitionId === MULTIATTACK_DEFENSE_EFFECT_ID && String(effect.sourceCombatantId) === String(attackerId) && String(effect.targetCombatantId) === String(targetId));
export const createMultiattackDefenseEffect = ({ attackerId, targetId, activeCombatantId, turnSequence }) => ({
  id: `multiattack-defense:${targetId}:${attackerId}:${turnSequence}`, definitionId: MULTIATTACK_DEFENSE_EFFECT_ID, name: 'Multiattack Defense', userVisible: false,
  sourceCombatantId: String(attackerId), targetCombatantId: String(targetId), stackKey: `${MULTIATTACK_DEFENSE_EFFECT_ID}:${targetId}:${attackerId}:${turnSequence}`, stackPolicy: 'ignore',
  appliedAtTurnSequence: turnSequence, removeOnSourceRemoval: false,
  expiration: { type: 'sourceTurn', combatantId: String(activeCombatantId), boundary: 'end', remainingOccurrences: 1, expireOnCurrentTurn: true },
  description: 'Disadvantage on other attack rolls against this Ranger this turn.',
});

/** Weapon kinds a Ranger masters; the count does not grow with level. */
export const rangerMasteryCount = level => (level < 1 ? 0 : 2);

/** Deft Explorer (level 2): Expertise in one proficient skill and two languages. */
export const DEFT_EXPLORER_LANGUAGE_COUNT = 2;
export const getDeftExplorerLanguages = character => (Array.isArray(character?.classState?.ranger?.deftExplorerLanguages)
  ? character.classState.ranger.deftExplorerLanguages : []);
export const needsDeftExplorerLanguages = character => hasRangerFeature(character, 'deftExplorer') && getDeftExplorerLanguages(character).length < DEFT_EXPLORER_LANGUAGE_COUNT;
